import { StarsButton, Translation } from '@ps/web-shared-components';
import { StarsInfo, createForm } from '@ps/web-shared-ui';
import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { styles as sharedStyles } from '@swf/account';
import { select } from '@swf/core/store';

import View from '../View/View';

import AccountDetails from './AccountDetails/AccountDetails';
import AccountDetailsSummary from './AccountDetailsSummary/AccountDetailsSummary';
import ForcedOccupation from './ForcedOccupation/ForcedOccupation';
import {
  TXTCLI_Apply__forChanges,
  TXTWCL_Go_back,
  TXTWCL_Occupation,
  TXTWCL_The_service_is_unavailable_rig_ELL_1,
  TXTWCL_Your_changes_have_been_saved_s_ELL
} from './Occupation.messages.json';
import styles from './Occupation.scss';
import OccupationDetails from './OccupationDetails/OccupationDetails';
import OccupationSummary from './OccupationSummary/OccupationSummary';
import PEPHIODetails from './PEPHIODetails/PEPHIODetails';
import PEPHIOSelection, { PEPHIOOptions } from './PEPHIOSelection/PEPHIOSelection';
import useOccupation from './hooks/useOccupation';

export interface FormValues {
  pepHioSelection: PEPHIOOptions;
  occupation: string;
  companyName?: string;
  dialingCode: string;
  companyPhone?: string;
  companyAddress?: string;
  pepHioName?: string;
  relationshipToPepHio?: string;
  sourceOfFunds?: string;
  sourceOfWealth?: string;
  name?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  state?: string;
  country?: string;
  countryCode: string;
  phone?: string;
  confirmCheckbox: boolean;
}

const Form = createForm<FormValues>();

enum Screens {
  ForcedOccupation,
  Selection,
  Summary,
  Form
}

export const MERGED_OCCUPATION = '/notifications/merged-occupation';

interface OccupationComponent
  extends React.FC<{ notification?: boolean; setOccupationTitle?: (value: boolean) => void }> {
  PEPHIOSelection: typeof PEPHIOSelection;
}

const Occupation: OccupationComponent = ({ notification, setOccupationTitle }) => {
  const occupationInfo = useSelector(select.account.getOccupationInfo);
  const { initialValues, onSubmit } = useOccupation(notification);
  const [currentScreen, setCurrentScreen] = useState<Screens>();
  const checkboxRef = useRef(false);
  const goBack = (screen: Screens) => setCurrentScreen(screen);
  const mergedOccupation = useSelector(select.account.getWebClientDialog) === MERGED_OCCUPATION;

  useEffect(() => {
    switch (true) {
      case mergedOccupation:
        return setCurrentScreen(Screens.ForcedOccupation);
      case !initialValues.pepHioSelection ||
        initialValues.pepHioSelection === PEPHIOOptions.NotSelected:
        return setCurrentScreen(Screens.Selection);
      default:
        return setCurrentScreen(Screens.Summary);
    }
  }, [initialValues.pepHioSelection, mergedOccupation]);

  useEffect(() => {
    setOccupationTitle?.(currentScreen === Screens.ForcedOccupation);
  }, [currentScreen, setOccupationTitle]);

  if (isEmpty(occupationInfo)) {
    return null;
  }

  return (
    <View
      title={!notification && <Translation {...TXTWCL_Occupation} />}
      className={classNames({ [styles.modal]: notification })}
    >
      <Form
        onSubmit={onSubmit}
        initialValues={{ ...initialValues, confirmCheckbox: checkboxRef.current }}
        className={classNames({ [sharedStyles.restrictedWidth]: !notification })}
        render={({
          dirtySinceLastSubmit,
          hasSubmitErrors,
          pristine,
          submitError,
          submitSucceeded,
          submitting,
          values: { pepHioSelection }
        }) => (
          <>
            {currentScreen === Screens.ForcedOccupation && notification && (
              <ForcedOccupation onContinue={() => setCurrentScreen(Screens.Summary)} />
            )}
            {currentScreen === Screens.Summary && notification && <AccountDetailsSummary />}
            {currentScreen === Screens.Summary && (
              <OccupationSummary
                onEdit={() => setCurrentScreen(Screens.Selection)}
                notification={notification}
              />
            )}
            {currentScreen === Screens.Selection && (
              <PEPHIOSelection onContinue={() => setCurrentScreen(Screens.Form)} />
            )}
            {currentScreen === Screens.Form && (
              <>
                {submitSucceeded && (
                  <StarsInfo type="confirmation" className={styles.message}>
                    <Translation {...TXTWCL_Your_changes_have_been_saved_s_ELL} />
                  </StarsInfo>
                )}
                {submitError && (
                  <StarsInfo type="error" className={styles.message}>
                    <Translation {...TXTWCL_The_service_is_unavailable_rig_ELL_1} />
                  </StarsInfo>
                )}
                <OccupationDetails />
                {pepHioSelection === PEPHIOOptions.PEPOrHIORelated && <PEPHIODetails />}
                {notification && <AccountDetails checkboxValue={checkboxRef} />}
                <StarsButton
                  block
                  loading={submitting}
                  disabled={pristine || (hasSubmitErrors && !dirtySinceLastSubmit)}
                  type="submit"
                  className={sharedStyles.submit}
                  translation={TXTCLI_Apply__forChanges}
                />
              </>
            )}
            {notification &&
              (currentScreen === Screens.Selection || currentScreen === Screens.Form) && (
                <StarsButton
                  block
                  variant="secondary"
                  onClick={() =>
                    goBack(
                      currentScreen === Screens.Selection ? Screens.Summary : Screens.Selection
                    )
                  }
                  translation={TXTWCL_Go_back}
                />
              )}
          </>
        )}
      />
    </View>
  );
};

Occupation.PEPHIOSelection = PEPHIOSelection;

export default Occupation;
